import axios, { AxiosRequestConfig } from 'axios';
import { toast } from 'react-toastify';
import Cookies from 'universal-cookie';

const cookies = new Cookies();
const isProduction = process.env.NODE_ENV === 'production';

export const setCookie = (name: string, value: string | number, minutes: number) => {
    const expiryDate = new Date(Date.now() + minutes * 60 * 1000);
    cookies.set(name, value, { path: '/', expires: expiryDate, secure: isProduction });
};

export const getCookie = (name: string) => cookies.get(name);
const removeCookie = (name: string) => cookies.remove(name, { path: '/' });

const setToken = (name: string, token: string, expiresInMinutes: number) => {
    setCookie(name, token, expiresInMinutes);
    setCookie(`${name}_expiry`, Math.floor(Date.now() / 1000) + expiresInMinutes * 60, expiresInMinutes);
};

export const clearTokens = () => {
    ['access_token', 'refresh_token', 'username', 'refresh_token_expiry', 'is_owner'].forEach(removeCookie);
};

export const login = async (username: string, password: string): Promise<void> => {
    try {
        const { data } = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/token/`, { username, password });
        setToken('access_token', data.access, 50);
        setToken('refresh_token', data.refresh, 365 * 24 * 60);
        setCookie('username', data.username, 365 * 24 * 60);
        setCookie('is_owner', data.is_owner, 365 * 24 * 60);
    } catch (error) {
        throw error;
    }
};

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null; 
export const refresh = async (): Promise<string | null> => {
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }
    try {
        isRefreshing = true;
        const refreshToken = getCookie("refresh_token");
        const refreshTokenExpiry = getCookie("refresh_token_expiry");
        const currentTime = Math.floor(Date.now() / 1000);
        if (!refreshToken || (refreshTokenExpiry && currentTime >= Number(refreshTokenExpiry))) {
            clearTokens();
            window.location.href = '/login';
            throw new Error("Refresh token expired or not available");
        }
        refreshPromise = axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/token/refresh/`, { refresh: refreshToken })
            .then((response) => {
                setToken('access_token', response.data.access, 50);
                setToken('refresh_token', response.data.refresh, 365 * 24 * 60);
                setCookie('username', getCookie("username"), 365 * 24 * 60);
                setCookie('is_owner', getCookie("is_owner"), 365 * 24 * 60);
                return response.data.access;
            })
            .catch((error) => {
                if (error.response?.status === 401) {
                    clearTokens();
                    window.location.href = '/login';
                }
                throw error;
            })
            .finally(() => {
                isRefreshing = false;
                refreshPromise = null;
            });
        return refreshPromise;
    } catch (error: any) {
        isRefreshing = false;
        refreshPromise = null;
        throw error;
    }
};

const makeRequest = async (type: string, endpoint: string, data?: any, config?: AxiosRequestConfig) => {
    const url = `${process.env.NEXT_PUBLIC_API_URL}${endpoint}`;
    switch (type.toUpperCase()) {
        case "GET":
            return (await axios.get(url, config)).data;
        case "POST":
            return (await axios.post(url, data, config)).data;
        case "PUT":
        case "UPDATE":
            return (await axios.put(url, data, config)).data;
        case "DELETE":
            return (await axios.delete(url, config)).data;
        default:
            throw new Error(`Unsupported request type: ${type}`);
    }
};

export const sendRequest = async (type: string, endpoint: string, data?: any): Promise<any> => {
    try {
        let accessToken = getCookie('access_token');
        if (!accessToken || Number(getCookie('access_token_expiry')) <= Date.now() / 1000) {
            accessToken = await refresh();
            if (!accessToken) return null;
        }
        return await makeRequest(type, endpoint, data, { headers: { Authorization: `Bearer ${accessToken}` } });
    } catch (error:any) {
        if (error.response?.status === 400) {
            toast.error(error.response.data.message)
        }
        if (error.response?.status === 401) {
            clearTokens();
            window.location.href = '/login';
        }
        return null;
    }
};

export const checkTokenSendRequest = async (type: string, endpoint: string, data?: any): Promise<any> => {
    if (!getCookie('access_token') && !getCookie('refresh_token')) {
        return sendRequestNoToken(type, endpoint, data);
    }
    return sendRequest(type, endpoint, data);
};


export const sendRequestNoToken = async (type: string, endpoint: string, data?: any): Promise<any> => {
    return makeRequest(type, endpoint, data);
};

export const logout = async (): Promise<void> => {
    try {
        const refreshToken = getCookie('refresh_token');
        if (refreshToken) await sendRequest("POST","/api/token/logout/", { refresh: refreshToken });
    } catch (error) {
        console.error('Logout error:', error);
    } finally {
        clearTokens();
        window.location.href = '/';
    }
};
